import { useState } from "react"
import { doc, setDoc, getDoc } from "firebase/firestore"
import { db } from "../../services/firebase"

// Columnas esperadas del CSV de Sheets:
// ID, Nombre y Apellido, Telefono, Origen, Aportes en Low Travel ($),
// Integracion Por, Fecha de Nacimiento, Ayuda Cumpleaños, Dias para el Cumpleaños,
// Edad, Veces que Contrató, Puntos por Aporte, Puntos por Feedback,
// Puntos por Referidos (Max 3), Puntos por compra de referidos,
// Puntos por Cumpleaños, Puntos Usados, Total de Puntos

function parsearCSV(texto) {
  const lineas = texto.trim().split("\n").filter(l => l.trim())
  if (lineas.length < 2) return []

  const encabezados = lineas[0].split(",").map(h => h.trim().toLowerCase()
    .replace(/[áàä]/g, "a").replace(/[éèë]/g, "e")
    .replace(/[íìï]/g, "i").replace(/[óòö]/g, "o")
    .replace(/[úùü]/g, "u").replace(/[^a-z0-9\s]/g, "").trim()
  )

  return lineas.slice(1).map(linea => {
    const valores = linea.split(",").map(v => v.trim().replace(/^"|"$/g, ""))
    const fila = {}
    encabezados.forEach((h, i) => { fila[h] = valores[i] || "" })
    return fila
  }).filter(fila => fila["id"] && fila["nombre y apellido"])
}

function mapearCliente(fila) {
  const nombreCompleto = fila["nombre y apellido"] || ""
  const partes = nombreCompleto.trim().split(" ")
  const nombre   = partes[0] || ""
  const apellido = partes.slice(1).join(" ") || ""

  const num = (v) => {
    const n = parseFloat((v || "0").replace(",", "."))
    return isNaN(n) ? 0 : n
  }

  return {
    nombre,
    apellido,
    nombre_completo:   nombreCompleto,
    telefono:          fila["telefono"]      || "",
    ciudad:            fila["origen"]        || "",
    nacimiento:        fila["fecha de nacimiento"] || "",
    contrataciones:    num(fila["veces que contrato"]),
    aportes_total:     num(fila["aportes en low travel "]),
    puntos_aporte:     num(fila["puntos por aporte"]),
    puntos_feedback:   num(fila["puntos por feedback"]),
    puntos_referidos:  num(fila["puntos por referidos max 3"]),
    puntos_compra_ref: num(fila["puntos por compra de referidos"]),
    puntos_cumpleanos: num(fila["puntos por cumpleanos"]),
    puntos_usados:     num(fila["puntos usados"]),
    estado:            "prospecto",
    importado_en:      new Date(),
  }
}

export default function ImportadorCSV({ onImportado }) {
  const [csv, setCsv]             = useState("")
  const [preview, setPreview]     = useState([])
  const [importando, setImportando] = useState(false)
  const [resultado, setResultado] = useState(null)
  const [error, setError]         = useState("")
  const [paso, setPaso]           = useState("pegar") // pegar | preview | listo

  function handleParsear() {
    setError("")
    if (!csv.trim()) return setError("Pega el contenido del CSV primero.")
    const filas = parsearCSV(csv)
    if (filas.length === 0) return setError("No se encontraron datos validos. Verifica el formato del CSV.")
    setPreview(filas.map(f => ({ id: f["id"], cliente: mapearCliente(f) })))
    setPaso("preview")
  }

  async function handleImportar() {
    setImportando(true)
    setError("")
    let ok = 0, saltados = 0, errores = 0

    for (const { id, cliente } of preview) {
      try {
        const ref  = doc(db, "usuarios_sheets", id.toString())
        const snap = await getDoc(ref)
        if (snap.exists()) {
          // Actualiza solo los campos del sheets, preserva subcolecciones
          await setDoc(ref, cliente, { merge: true })
          saltados++
        } else {
          await setDoc(ref, cliente)
          ok++
        }
      } catch (e) {
        console.error("Error importando id " + id, e)
        errores++
      }
    }

    setResultado({ ok, saltados, errores, total: preview.length })
    setPaso("listo")
    setImportando(false)
    if (onImportado) onImportado()
  }

  const inputStyle = {
    width: "100%", padding: "10px 14px", background: "#1a1a1a",
    border: "1px solid #3a3a3a", borderRadius: "8px", color: "#e5e7eb",
    fontFamily: "Inter, sans-serif", fontSize: "0.875rem", outline: "none",
    resize: "vertical"
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>

      {/* INSTRUCCIONES */}
      <div style={{ background: "#0f2340", border: "1px solid #1e3a5f", borderRadius: "12px", padding: "20px" }}>
        <div style={{ fontWeight: 700, color: "#3b82f6", marginBottom: "10px" }}>
          Como exportar desde Google Sheets
        </div>
        {["1. Abri tu Sheets con los datos de clientes",
          "2. Archivo → Descargar → Valores separados por comas (.csv)",
          "3. Abri el archivo .csv con el Bloc de notas",
          "4. Selecciona todo (Ctrl+A) y copia (Ctrl+C)",
          "5. Pega el contenido en el campo de abajo"
        ].map((paso, i) => (
          <div key={i} style={{ fontSize: "0.875rem", color: "#9ca3af", marginBottom: "4px" }}>{paso}</div>
        ))}
      </div>

      {paso === "pegar" && (
        <>
          <div>
            <div style={{ fontSize: "0.8rem", fontWeight: 700, color: "#6b7280", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
              Pegar contenido del CSV
            </div>
            <textarea
              rows={10}
              placeholder={"ID,Nombre y Apellido,Telefono,Origen,...\n1,Juan Perez,1155667788,Buenos Aires,..."}
              value={csv}
              onChange={e => setCsv(e.target.value)}
              style={inputStyle}
            />
          </div>
          {error && (
            <div style={{ padding: "12px 16px", background: "#2a0a0a", border: "1px solid #ef444440", borderRadius: "8px", color: "#ef4444", fontSize: "0.875rem" }}>
              {error}
            </div>
          )}
          <button
            onClick={handleParsear}
            style={{ padding: "12px", background: "#3b82f6", border: "none", borderRadius: "8px", color: "white", fontFamily: "Inter, sans-serif", fontSize: "0.95rem", fontWeight: 700, cursor: "pointer" }}
          >
            Verificar datos
          </button>
        </>
      )}

      {paso === "preview" && (
        <>
          <div style={{ background: "#0a2a1a", border: "1px solid #10b98140", borderRadius: "10px", padding: "14px 18px" }}>
            <div style={{ fontWeight: 700, color: "#10b981", marginBottom: "4px" }}>
              Se encontraron {preview.length} clientes listos para importar
            </div>
            <div style={{ fontSize: "0.8rem", color: "#6b7280" }}>
              Los clientes que ya existen seran actualizados. Los nuevos seran creados.
            </div>
          </div>

          <div style={{ background: "#0f0f0f", border: "1px solid #2a2a2a", borderRadius: "12px", overflow: "hidden", maxHeight: "360px", overflowY: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#0a0a0a" }}>
                  {["ID", "Nombre", "Ciudad", "Contrataciones", "Puntos aporte", "Total pts"].map(h => (
                    <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontSize: "0.75rem", fontWeight: 700, color: "#4b5563", textTransform: "uppercase", borderBottom: "1px solid #2a2a2a" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.map(({ id, cliente }) => {
                  const total = (cliente.puntos_aporte || 0) + (cliente.puntos_feedback || 0) +
                    (cliente.puntos_referidos || 0) + (cliente.puntos_compra_ref || 0) +
                    (cliente.puntos_cumpleanos || 0) - (cliente.puntos_usados || 0)
                  return (
                    <tr key={id}>
                      <td style={{ padding: "10px 14px", color: "#6b7280", fontSize: "0.8rem", borderBottom: "1px solid #1a1a1a" }}>{id}</td>
                      <td style={{ padding: "10px 14px", color: "#e5e7eb", fontWeight: 600, fontSize: "0.875rem", borderBottom: "1px solid #1a1a1a" }}>{cliente.nombre} {cliente.apellido}</td>
                      <td style={{ padding: "10px 14px", color: "#9ca3af", fontSize: "0.875rem", borderBottom: "1px solid #1a1a1a" }}>{cliente.ciudad || "—"}</td>
                      <td style={{ padding: "10px 14px", color: "#9ca3af", fontSize: "0.875rem", borderBottom: "1px solid #1a1a1a" }}>{cliente.contrataciones}</td>
                      <td style={{ padding: "10px 14px", color: "#3b82f6", fontWeight: 600, fontSize: "0.875rem", borderBottom: "1px solid #1a1a1a" }}>{cliente.puntos_aporte} pts</td>
                      <td style={{ padding: "10px 14px", color: "#f59e0b", fontWeight: 700, fontSize: "0.875rem", borderBottom: "1px solid #1a1a1a" }}>{total} pts</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          <div style={{ display: "flex", gap: "10px" }}>
            <button
              onClick={() => { setPaso("pegar"); setPreview([]) }}
              style={{ flex: 1, padding: "12px", background: "#1a1a1a", border: "1px solid #3a3a3a", borderRadius: "8px", color: "#9ca3af", fontFamily: "Inter, sans-serif", fontWeight: 700, cursor: "pointer" }}
            >
              Volver a editar
            </button>
            <button
              onClick={handleImportar}
              disabled={importando}
              style={{ flex: 2, padding: "12px", background: importando ? "#1a1a1a" : "#10b981", border: "none", borderRadius: "8px", color: importando ? "#6b7280" : "white", fontFamily: "Inter, sans-serif", fontSize: "0.95rem", fontWeight: 700, cursor: importando ? "not-allowed" : "pointer" }}
            >
              {importando ? "Importando..." : "Importar " + preview.length + " clientes a Firestore"}
            </button>
          </div>
        </>
      )}

      {paso === "listo" && resultado && (
        <div style={{ background: "#0a2a1a", border: "1px solid #10b98140", borderRadius: "12px", padding: "28px", textAlign: "center" }}>
          <div style={{ fontSize: "2.5rem", marginBottom: "12px" }}>✓</div>
          <div style={{ fontWeight: 800, color: "#10b981", fontSize: "1.1rem", marginBottom: "16px" }}>
            Importacion completada
          </div>
          <div style={{ display: "flex", justifyContent: "center", gap: "24px", marginBottom: "20px" }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: "1.8rem", fontWeight: 800, color: "#10b981" }}>{resultado.ok}</div>
              <div style={{ fontSize: "0.8rem", color: "#6b7280" }}>Nuevos</div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: "1.8rem", fontWeight: 800, color: "#3b82f6" }}>{resultado.saltados}</div>
              <div style={{ fontSize: "0.8rem", color: "#6b7280" }}>Actualizados</div>
            </div>
            {resultado.errores > 0 && (
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: "1.8rem", fontWeight: 800, color: "#ef4444" }}>{resultado.errores}</div>
                <div style={{ fontSize: "0.8rem", color: "#6b7280" }}>Errores</div>
              </div>
            )}
          </div>
          <button
            onClick={() => { setPaso("pegar"); setCsv(""); setPreview([]); setResultado(null) }}
            style={{ padding: "10px 24px", background: "#1a1a1a", border: "1px solid #3a3a3a", borderRadius: "8px", color: "#9ca3af", fontFamily: "Inter, sans-serif", fontWeight: 700, cursor: "pointer" }}
          >
            Importar otro CSV
          </button>
        </div>
      )}

    </div>
  )
}